import { lazy } from 'react';

// a chunk request that fails is not a slow route, it is a dead one: React.lazy caches the rejected
// promise, so every later render of that route re-throws the same failure for the rest of the session.
// measured on a throttled connection, the encounter chunk failed once and the page never recovered
const RETRY_DELAYS_MS = [400, 1200, 3000];

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// the error is rethrown untouched once the retries are spent, rather than reloading here. src/ErrorBoundary.js
// already owns that: it recognizes ChunkLoadError, reloads once behind its own sessionStorage guard, and
// falls back to a refresh screen. A second reload guard under a different key would let one failed chunk
// reload the document twice. What this adds is the retry the boundary never had - a chunk that fails once
// on a weak connection now recovers silently instead of surfacing as a full-page error.
//
// exported so a caller that must DEGRADE rather than fail can wrap the same retrying load
export const withChunkRetry = (importFactory) => async () => {
	for (let attempt = 0; ; attempt += 1) {
		try {
			return await importFactory();
		} catch (error) {
			if (attempt >= RETRY_DELAYS_MS.length) {
				throw error;
			}

			await wait(RETRY_DELAYS_MS[attempt]);
		}
	}
};

export const lazyWithRetry = (importFactory) => {
	const load = withChunkRetry(importFactory);

	const LazyComponent = lazy(load);
	// warming this before the route is reached puts the module in the bundler's cache, so the swap
	// finds it already resolved. failures are swallowed - a preload that cannot run must never be
	// what breaks the page that triggered it
	LazyComponent.preload = () => load().catch(() => { });

	return LazyComponent;
};

// For OPTIONAL ui only. React.lazy has no error path, so a chunk that will not load rethrows past every
// Suspense boundary to the nearest ErrorBoundary - which for the funnel's wallet buttons means one dead
// Apple Pay chunk replaces the whole checkout with the refresh screen while the card form beside it was
// working. After the retries are spent this resolves to a component that renders nothing, so the button
// disappears and the page carries on. A ROUTE must never use this: a blank page that reports nothing is
// worse than the refresh screen.
export const lazyOptional = (importFactory) => lazy(() => withChunkRetry(importFactory)().catch(() => ({ default: () => null })));

export default lazyWithRetry;
